import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Bell, Check, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function relativeTime(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const TYPE_META = {
  lock_unlock_request: { color: 'text-amber-400',    dot: 'bg-amber-500' },
  lock_unlock_status:  { color: 'text-blue-400',     dot: 'bg-blue-500' },
  installment_payment: { color: 'text-emerald-400',  dot: 'bg-emerald-500' },
  payment_received:    { color: 'text-emerald-400',  dot: 'bg-emerald-500' },
  commission_credit:   { color: 'text-emerald-400',  dot: 'bg-emerald-500' },
  commission_debit:    { color: 'text-red-400',      dot: 'bg-red-500' },
  cnic_expiry:         { color: 'text-orange-400',   dot: 'bg-orange-500' },
  lock_query_auto:     { color: 'text-orange-400',   dot: 'bg-orange-500' },
};

// Iter40 — notification types that resolve to a payment receipt PDF
const PAYMENT_TYPES = new Set(['installment_payment', 'payment_received']);

/** Bell icon + popover with the current user's in-app notifications.
 *  Polls /api/notifications every 30 seconds. */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications?limit=30`, { withCredentials: true });
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30 * 1000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const markAllRead = async () => {
    if (!unread) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/notifications/mark-read`, {}, { withCredentials: true });
      load();
    } catch (e) { toast.error('Failed to mark read'); }
    finally { setBusy(false); }
  };

  const markOneRead = async (id) => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-read`, { id }, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  const deleteOne = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${id}`, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  // Iter40 — Click a payment notification → opens the receipt PDF in a new tab.
  // Other notification types just get marked read.
  const openNotification = async (n) => {
    if (!n.is_read) markOneRead(n.id);
    if (!PAYMENT_TYPES.has(n.type)) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/api/notifications/${n.id}/receipt`,
        { withCredentials: true });
      if (data?.pdf) {
        const w = window.open('', '_blank');
        if (w) {
          w.document.write(
            `<iframe src="${data.pdf}" frameborder="0" style="width:100%;height:100vh"></iframe>`);
        }
      } else {
        toast.error('No receipt found for this notification.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open the receipt.');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell"
          className="relative p-2 rounded-sm text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end" sideOffset={8}
        className="w-[360px] bg-slate-900 border-slate-800 text-white p-0"
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
          <div className="text-sm font-semibold">Notifications</div>
          <button onClick={markAllRead}
            disabled={busy || unread === 0}
            data-testid="notification-mark-all-read"
            className="text-xs text-amber-400 hover:text-amber-300 disabled:opacity-50 flex items-center gap-1">
            <Check className="h-3 w-3" /> Mark all read
          </button>
        </div>
        {items.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-xs" data-testid="notification-empty">
            You're all caught up
          </div>
        ) : (
          <ScrollArea className="max-h-[420px]">
            <ul className="divide-y divide-slate-800">
              {items.map((n) => {
                const meta = TYPE_META[n.type] || { color: 'text-slate-300', dot: 'bg-slate-500' };
                return (
                  <li key={n.id}
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => openNotification(n)}
                    className={`px-3 py-2 group hover:bg-slate-800/60 transition-colors ${n.is_read ? 'opacity-70' : ''} ${PAYMENT_TYPES.has(n.type) ? 'cursor-pointer' : ''}`}>
                    <div className="flex items-start gap-2">
                      <div className={`w-2 h-2 mt-1.5 rounded-full ${n.is_read ? 'bg-slate-600' : meta.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${meta.color} truncate`}>{n.title}</div>
                        {n.body && <div className="text-xs text-slate-400 mt-0.5">{n.body}</div>}
                        <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2">
                          <span>{relativeTime(n.created_at)}</span>
                          {PAYMENT_TYPES.has(n.type) && (
                            <span className="text-emerald-400">· Click to open receipt PDF</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity"
                           onClick={(e) => e.stopPropagation()}>
                        {!n.is_read && (
                          <button onClick={() => markOneRead(n.id)}
                            title="Mark read"
                            className="text-slate-400 hover:text-emerald-400 p-1">
                            <Check className="h-3 w-3" />
                          </button>
                        )}
                        <button onClick={() => deleteOne(n.id)}
                          title="Delete"
                          className="text-slate-400 hover:text-red-400 p-1">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
